import { Link } from "@tanstack/react-router";
import { Phone, MessageCircle, FileText } from "lucide-react";
import { COMPANY } from "@/lib/company";
import { createWhatsAppUrl } from "@/lib/utils/whatsapp";
import { useI18n } from "@/context/I18nContext";

/**
 * The three ways to start a conversation, pinned to the bottom on phones.
 *
 * Most of this site's traffic is mobile, and until now every route to contact
 * lived either in a collapsed hamburger menu or at the very bottom of a long
 * page. A B2B buyer who has just read a spec table and decided to ask a
 * question had to go looking for the way to ask it.
 *
 * **Phones only.** On a desktop the header is always visible and already
 * carries these; a bar bolted to the bottom of a wide screen is the pattern
 * that makes a site feel like an app nobody asked for.
 *
 * **Three actions, not one.** Which channel a buyer wants is not ours to
 * decide: an İzmir hatchery rings, a Gulf importer opens WhatsApp, a European
 * purchasing office wants a written quote it can forward internally. Each is
 * one tap. The quote is styled as the primary because it is the one that
 * produces a tracked lead in Odoo rather than a conversation in someone's
 * phone.
 *
 * **Not the old FloatingQuoteCTA.** That component was unmounted from
 * SiteShell because it was the last persistent entry into the legacy
 * quote-cart (see CartContext.tsx). This links to `/request-quote`, the real
 * form, and touches no cart.
 */
export function MobileActionBar() {
  const { t } = useI18n();

  // No customMessage: the helper's own default already opens with the
  // "contacting you from the VARS B2B Portal … please connect me with an
  // export specialist" text, which is the right opener for a bar that is not
  // attached to any particular product.
  const whatsappUrl = createWhatsAppUrl({});

  return (
    <div
      className={[
        "fixed inset-x-0 bottom-0 z-40 md:hidden",
        // Sits below the mobile nav and the consent banner rather than
        // fighting them — both cover the screen when open, and a row of
        // buttons floating over a legal notice is how consent gets misclicked.
        "[.mobile-nav-open_&]:hidden [.consent-open_&]:hidden",
        "border-t border-border/70 bg-background/95 backdrop-blur-md",
        "shadow-[0_-4px_20px_rgba(0,0,0,0.10)]",
      ].join(" ")}
      // The home indicator on a modern phone overlaps the bottom of the
      // viewport; without this the buttons sit under it.
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="grid grid-cols-3 gap-2 px-3 py-2.5">
        <a
          href={COMPANY.phoneHref}
          data-analytics-surface="mobile-action-bar"
          // 44px minimum on every target: these are thumb-sized by
          // definition, and WCAG 2.5.8 asks for 24 as a floor, not a goal.
          className="flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-xl border border-border bg-background text-navy transition-colors active:bg-muted"
        >
          <Phone className="h-[18px] w-[18px] text-primary" aria-hidden="true" />
          <span className="text-xs font-bold">{t("actionBar.call")}</span>
        </a>

        <a
          href={whatsappUrl}
          data-analytics-surface="mobile-action-bar"
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-xl border border-emerald-600/30 bg-emerald-50 text-emerald-800 transition-colors active:bg-emerald-100"
        >
          <MessageCircle className="h-[18px] w-[18px]" aria-hidden="true" />
          <span className="text-xs font-bold">WhatsApp</span>
        </a>

        <Link
          to="/request-quote"
          className="flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-xl bg-primary text-primary-foreground shadow-sm transition-colors active:bg-primary/90"
        >
          <FileText className="h-[18px] w-[18px]" aria-hidden="true" />
          <span className="text-xs font-bold">{t("actionBar.quote")}</span>
        </Link>
      </div>
    </div>
  );
}
